import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_BRIEF_CHARS,
  ONBOARDING_STATUS_MAP,
  type AgencyTemplateView,
  type ContractRow,
  type IntakeBoard,
  type PortfolioView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { WORDS, date, money, plural } from '../../lib/format';
import { Icon, type IconName } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, Notice, PageHead } from '../../components/ui';
import { Avatar } from '../../components/People';
import { AccountPicker } from '../../components/RecordPicker';
import { Attachments } from '../../components/Attachments';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { ClientPreviewModal, ContractPreviewModal } from './Previews';

/**
 * العقود والتجهيز.
 *
 * الشاشةُ قسمان لأنّ السؤالين مختلفان: «ما الذي وقّعناه ولم نبدأه؟»
 * و«ما الذي نعمل عليه الآن وأين وصل؟». وخلطُهما في قائمةٍ واحدة يجعل
 * عقدًا مدفوعًا لم يبدأ يبدو كعقدٍ يجري عليه العمل.
 *
 * ## مشروعٌ واحدٌ لكلّ عقد
 *
 * كان التجهيزُ يُنشئ مشروعَه المستقلَّ («انضمام فلان») ثمّ يُنشأ للعقد
 * نفسِه مشروعُ عملٍ ثانٍ — فيصير للالتزام الواحد لوحان: تقدّمان وفريقان،
 * ولا يُعرف أيّهما الحقيقة. فصارت خطواتُ التجهيز مهامَّ **داخل** مشروع
 * العقد، وبطاقةُ الانضمام تحمل البريفَ وتقيس التقدّم من مهام المشروع
 * نفسِه.
 */
export function AgencyOnboarding() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { atLeast } = useAuth();
  const [starting, setStarting] = useState(false);
  const [briefOf, setBriefOf] = useState<ContractRow | null>(null);
  const [previewClient, setPreviewClient] = useState<string | null>(null);
  const [previewContract, setPreviewContract] = useState<string | null>(null);
  const [spinFor, setSpinFor] = useState<ContractRow | null>(null);

  const board = useQuery({
    queryKey: ['agency', 'intake-board'],
    queryFn: () => api<IntakeBoard>('/agency/intake/board'),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  /*
   * بدءُ التجهيز — أو إلحاقُ بطاقته بمشروعٍ قائم.
   *
   * بلا عقدٍ: يُنشئ مشروعًا تجريبيًّا وبطاقتَه. ومع عقدٍ له مشروعٌ أُنشئ
   * يدويًّا: يُلحق البطاقةَ بمشروعه القائم (لا يُنشئ جارًا له) فيحمل البريفَ
   * وخطواتِ التجهيز كأقرانه المفعَّلين تلقائيًّا — فلا تبقى بطاقةٌ شاذّة.
   */
  const start = useMutation({
    mutationFn: (vars: { accountId: string; contractId?: string }) =>
      api<{ id: string; existing: boolean }>('/agency/onboarding', {
        method: 'POST',
        body: vars,
      }),
    onSuccess: (r, vars) => {
      setStarting(false);
      refresh();
      toast(
        r.existing
          ? 'لهذا العقد بطاقةُ تجهيزٍ بالفعل'
          : vars.contractId
            ? 'أُضيفت بطاقةُ التجهيز إلى مشروعه'
            : 'بدأ التجهيز — أُنشئ مشروعه',
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر البدء.'),
  });

  const d = board.data;
  const waiting = d?.waiting ?? [];
  const running = d?.running ?? [];
  const duplicated = d?.duplicated ?? [];

  return (
    <div className="page">
      <PageHead
        title="العقود والتجهيز"
        subtitle="لكلّ عقدٍ مشروعٌ واحد: يُنشأ فتبدأ خطواتُ التجهيز داخله، ثمّ يستمرّ عملًا يوميًّا"
        actions={
          atLeast('MANAGER') ? (
            <button className="btn" onClick={() => setStarting(true)}>
              <Icon name="rocket" size={15} />
              تجهيزٌ بلا عقد
            </button>
          ) : undefined
        }
      />

      {board.isError && <ErrorState error={board.error} onRetry={() => void board.refetch()} />}
      {board.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {d && (
        <>
          {/* إرثُ ما قبل القاعدة: عقدٌ له أكثر من مشروع */}
          {duplicated.length > 0 && (
            <Notice
              kind="warning"
              icon="alert"
              title={`${duplicated.length} من العقود له أكثرُ من مشروع`}
            >
              أُنشئت قبل قاعدة «مشروعٌ واحدٌ لكلّ عقد»، فصار للالتزام الواحد لوحان: تقدّمان لا
              يُعرف أيّهما الحقيقة. انقل مهامّ أحدهما إلى الآخر ثمّ أرشِفه — والدمجُ يدويٌّ عمدًا
              لأنّ لكلّ لوحٍ حقولَه.
              <span className="notice-names">
                {duplicated.flatMap((c) =>
                  c.projects.map((p) => (
                    <Link key={p.id} to={`/agency/projects/${p.id}`} className="notice-name">
                      {c.number} · {p.name}
                      <Icon name="chevronLeft" size={12} />
                    </Link>
                  )),
                )}
              </span>
            </Notice>
          )}

          {waiting.length === 0 && running.length === 0 && (
            <EmptyState
              title="لا عقود سارية"
              text="فعّل عقدًا من المبيعات فيظهر هنا ليُنشأ له مشروعُه — أو ابدأ تجهيزًا بلا عقدٍ لعميلٍ قائم."
              action={
                <Link to="/sales/contracts" className="btn btn-primary">
                  العقود
                </Link>
              }
            />
          )}

          {/* ١. عقودٌ جديدة تنتظر مشاريعها */}
          {waiting.length > 0 && (
            <section className="ag-sec">
              <div className="ag-sec-head">
                <h2 className="ag-sec-title">
                  <span className="ag-sec-ic is-wait">
                    <Icon name="clipboard" size={15} />
                  </span>
                  عقود جديدة
                  <span className="ag-sec-count num">{waiting.length}</span>
                </h2>
                <p className="tiny mute">
                  وُقّعت وفُعّلت ولم يبدأ العمل عليها — كلُّ يومٍ هنا يومٌ من مدّة العقد يمرّ.
                </p>
              </div>

              <div className="ag-ct-grid">
                {waiting.map((c) => (
                  <article key={c.id} className="ag-ct-card is-wait">
                    <ContractHead
                      c={c}
                      onPreviewClient={setPreviewClient}
                      onPreviewContract={setPreviewContract}
                    />
                    <div className="ag-ct-meta">
                      <span className="tiny mute">
                        <Icon name="calendar" size={12} /> بدأ {date(c.startAt)}
                      </span>
                      <span className="tiny mute num">
                        <Icon name="wallet" size={12} /> {money(c.value)}
                      </span>
                    </div>
                    {atLeast('MANAGER') ? (
                      <button className="btn btn-primary ag-ct-cta" onClick={() => setSpinFor(c)}>
                        <Icon name="rocket" size={14} />
                        أنشئ مشروعه
                      </button>
                    ) : (
                      <p className="tiny mute">ينتظر مديرًا لينشئ مشروعه.</p>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* ٢. عقودٌ سارية يعمل عليها */}
          {running.length > 0 && (
            <section className="ag-sec">
              <div className="ag-sec-head">
                <h2 className="ag-sec-title">
                  <span className="ag-sec-ic is-run">
                    <Icon name="briefcase" size={15} />
                  </span>
                  عقود سارية
                  <span className="ag-sec-count num">{running.length}</span>
                </h2>
                <p className="tiny mute">
                  لكلٍّ مشروعُه ولوحُه — والتقدّمُ محسوبٌ من مهامّه لا من تقديرٍ يُكتب.
                </p>
              </div>

              <div className="ag-ct-grid">
                {running.map((c) => {
                  const p = c.projects[0]!;
                  const pct = p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0;
                  const onb = c.onboarding;
                  const onbPct = onb?.taskCount
                    ? Math.round((onb.doneCount / onb.taskCount) * 100)
                    : 0;
                  return (
                    <article
                      key={c.id}
                      className={`ag-ct-card is-run${p.overdueCount > 0 ? ' is-late' : ''}`}
                    >
                      <ContractHead
                        c={c}
                        onPreviewClient={setPreviewClient}
                        onPreviewContract={setPreviewContract}
                      />

                      <Link to={`/agency/projects/${p.id}`} className="ag-ct-project">
                        <span className="ag-dot" style={{ background: p.color }} />
                        <span className="ag-ct-pname">{p.name}</span>
                        <Icon name="chevronLeft" size={13} />
                      </Link>

                      <div className="ag-progress" title={`${p.doneCount}/${p.taskCount}`}>
                        <span
                          className="ag-progress-fill"
                          style={{ width: `${pct}%`, background: p.color }}
                        />
                      </div>
                      <div className="ag-ct-meta">
                        <span className="tiny mute num">
                          {p.doneCount}/{p.taskCount} · {pct}٪
                        </span>
                        {p.overdueCount > 0 && (
                          <span className="ag-chip ag-chip-late num">
                            {p.overdueCount} متأخّرة
                          </span>
                        )}
                      </div>

                      {onb ? (
                        <div className="ag-ct-onb">
                          <span className="tiny">
                            <Icon name="rocket" size={12} /> التجهيز
                          </span>
                          <Badge kind={ONBOARDING_STATUS_MAP[onb.status].kind}>
                            {ONBOARDING_STATUS_MAP[onb.status].label}
                          </Badge>
                          <span className="tiny mute num">
                            {onb.doneCount}/{onb.taskCount} · {onbPct}٪
                          </span>
                          <button className="btn btn-sm" onClick={() => setBriefOf(c)}>
                            <Icon name="note" size={12} />
                            {onb.brief ? 'البريف' : 'اكتب البريف'}
                          </button>
                        </div>
                      ) : atLeast('MANAGER') ? (
                        <div className="ag-ct-onb">
                          <span className="tiny mute">
                            <Icon name="rocket" size={12} /> بلا بطاقة تجهيز — أُنشئ يدويًّا
                          </span>
                          <button
                            className="btn btn-sm"
                            onClick={() => start.mutate({ accountId: c.accountId, contractId: c.id })}
                            disabled={start.isPending}
                          >
                            <Icon name="plus" size={12} />
                            أضِف بطاقة تجهيز
                          </button>
                        </div>
                      ) : (
                        <p className="tiny mute">بلا بطاقة تجهيز — المشروع أُنشئ يدويًّا.</p>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {spinFor && (
        <SpinUpModal
          contract={spinFor}
          onClose={() => setSpinFor(null)}
          onCreated={(projectId) => {
            setSpinFor(null);
            refresh();
            toast('أُنشئ مشروع العقد — نقلناك إليه');
            navigate(`/agency/projects/${projectId}`);
          }}
        />
      )}

      {starting && (
        <Modal title="تجهيزٌ بلا عقد" onClose={() => setStarting(false)}>
          <p className="tiny mute mb-4">
            لعميلٍ قائمٍ يُعاد تجهيزه بلا عقدٍ جديد. يُنشأ مشروعٌ تجريبيّ بخطوات التجهيز
            الافتراضية مربوطًا بملفّه.
          </p>
          <AccountPicker onPick={(accountId) => start.mutate({ accountId })} />
        </Modal>
      )}

      {previewClient && (
        <ClientPreviewModal accountId={previewClient} onClose={() => setPreviewClient(null)} />
      )}
      {previewContract && (
        <ContractPreviewModal contractId={previewContract} onClose={() => setPreviewContract(null)} />
      )}

      {briefOf?.onboarding && (
        <BriefModal
          onboardingId={briefOf.onboarding.id}
          accountName={briefOf.accountName}
          initial={briefOf.onboarding.brief ?? ''}
          onClose={() => setBriefOf(null)}
          onSaved={() => {
            setBriefOf(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/** رأسُ بطاقة العقد: وجهُ العميل ورقمُ عقده — كلاهما يُفتح. */
function ContractHead({
  c,
  onPreviewClient,
  onPreviewContract,
}: {
  c: ContractRow;
  onPreviewClient: (id: string) => void;
  onPreviewContract: (id: string) => void;
}) {
  return (
    <div className="ag-ct-head">
      <Avatar name={c.accountName} src={c.accountAvatarUrl} size={40} />
      <div className="ag-ct-who">
        <button className="ag-ct-client ag-linklike" onClick={() => onPreviewClient(c.accountId)}>
          {c.accountName}
        </button>
        <button className="tiny mute num ag-linklike" onClick={() => onPreviewContract(c.id)}>
          {c.number} · {c.title}
        </button>
      </div>
    </div>
  );
}

/**
 * إنشاءُ مشروع العقد — واختيارُ قالبه.
 *
 * القالبُ يُسأل عنه هنا لا بعد الإنشاء: أقسامُ اللوح وحقولُ التفاصيل
 * تُنسخ لحظةَ الإنشاء، وتغييرُها بعد أن يكتب الفريقُ فيها بياناتٍ أصعب.
 */
function SpinUpModal({
  contract,
  onClose,
  onCreated,
}: {
  contract: ContractRow;
  onClose: () => void;
  onCreated: (projectId: string) => void;
}) {
  const toast = useToast();
  const [templateId, setTemplateId] = useState<string>('');
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [name, setName] = useState(contract.title);

  const templates = useQuery({
    queryKey: ['agency', 'templates', false],
    queryFn: () => api<AgencyTemplateView[]>('/settings/project-templates'),
  });
  /* المحفظةُ تُختار مع المشروع لا بعده — موضعٌ واحدٌ للقرار. */
  const portfolios = useQuery({
    queryKey: ['agency', 'portfolios'],
    queryFn: () => api<PortfolioView[]>('/agency/portfolios'),
  });

  const rows = templates.data ?? [];
  const pfRows = portfolios.data ?? [];
  const fallback = rows.find((t) => t.isDefault);
  const chosen = rows.find((t) => t.id === templateId) ?? fallback ?? null;

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/agency/projects', {
        method: 'POST',
        body: {
          name: name.trim(),
          kind: 'CONTRACT',
          accountId: contract.accountId,
          contractId: contract.id,
          templateId: templateId || null,
          portfolioId: portfolioId || null,
        },
      }),
    onSuccess: (p) => onCreated(p.id),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإنشاء.'),
  });

  return (
    <Modal title={`مشروع عقد ${contract.number}`} onClose={onClose}>
      <div className="ag-ct-head mb-4">
        <Avatar name={contract.accountName} src={contract.accountAvatarUrl} size={38} />
        <div className="ag-ct-who">
          <strong>{contract.accountName}</strong>
          <span className="tiny mute num">{money(contract.value)}</span>
        </div>
      </div>

      <label className="field">
        <span className="label">اسم المشروع</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>

      <div className="field">
        <span className="label">القالب</span>
        {templates.isPending ? (
          <div className="skeleton" style={{ height: 60 }} />
        ) : rows.length === 0 ? (
          <p className="tiny mute">
            لا قوالب بعد — سيُنشأ المشروع بالأقسام الأربعة الافتراضية.{' '}
            <Link to="/settings/project-templates">أنشئ قالبًا</Link> لتوحيد شكل مشاريعك.
          </p>
        ) : (
          <div className="tpl-pick">
            {rows.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`tpl-pick-btn${(templateId || fallback?.id) === t.id ? ' is-active' : ''}`}
                onClick={() => setTemplateId(t.id)}
              >
                <span className="tpl-icon" style={{ background: `${t.color}1a`, color: t.color }}>
                  <Icon name={t.icon as IconName} size={16} />
                </span>
                <span className="tpl-pick-body">
                  <b>{t.name}</b>
                  <span className="tiny mute">
                    {plural(t.sectionCount, WORDS.section)} · {plural(t.fieldCount, WORDS.field)} · {plural(t.taskCount, WORDS.task)}
                  </span>
                </span>
                {t.isDefault && <Badge kind="success">افتراضي</Badge>}
              </button>
            ))}
          </div>
        )}
      </div>

      {chosen?.description && <p className="tiny mute">{chosen.description}</p>}

      <label className="field">
        <span className="label">المحفظة</span>
        <select
          className="input"
          value={portfolioId}
          onChange={(e) => setPortfolioId(e.target.value)}
        >
          <option value="">
            {chosen?.portfolioName ? `محفظة القالب (${chosen.portfolioName})` : 'بلا محفظة'}
          </option>
          {pfRows.map((pf) => (
            <option key={pf.id} value={pf.id}>
              {pf.name}
            </option>
          ))}
        </select>
      </label>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => create.mutate()}
          disabled={name.trim().length < 2 || create.isPending}
        >
          {create.isPending ? 'جارٍ الإنشاء…' : 'أنشئ المشروع'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}

/** البريف المشترك — نافذة كتابةٍ وقراءة. */
function BriefModal({
  onboardingId,
  accountName,
  initial,
  onClose,
  onSaved,
}: {
  onboardingId: string;
  accountName: string;
  initial: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [brief, setBrief] = useState(initial);

  const save = useMutation({
    mutationFn: () => api(`/agency/onboarding/${onboardingId}`, { method: 'PATCH', body: { brief } }),
    onSuccess: () => {
      toast('حُفظ البريف');
      onSaved();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  return (
    <Modal title={`بريف ${accountName}`} onClose={onClose} wide>
      <textarea
        className="textarea"
        rows={12}
        value={brief}
        onChange={(e) => setBrief(e.target.value.slice(0, MAX_BRIEF_CHARS))}
        placeholder={
          'من هو العميل وماذا يبيع؟\nما أهدافه من التعاقد؟\nالجمهور المستهدف والنبرة؟\nمحاذير يجب ألا نقع فيها؟'
        }
        autoFocus
      />
      <p className="tiny mute mt-2 num">
        {brief.length}/{MAX_BRIEF_CHARS}
      </p>

      <div className="mt-4">
        <Attachments entity="onboarding" entityId={onboardingId} title="ملفات البريف" />
      </div>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'جارٍ الحفظ…' : 'حفظ البريف'}
        </button>
        <button className="btn" onClick={onClose}>
          إغلاق
        </button>
      </div>
    </Modal>
  );
}
